import { action, atom, withAssign, withInit } from '@reatom/framework'

import { AsyncStateN } from '@blade/std-lib'

import * as TableFilter from './table-filter'

import type { DataTableStateModel } from './reatom-data-table-state'
import type { Column } from '../ui/data-table-types'
import type { Ctx } from '@reatom/framework'

export type TableFilterOptions<ColumnId extends string = string> = {
  initialFilters?:
    | TableFilter.TableFilters<ColumnId>
    | ((ctx: Ctx) => TableFilter.TableFilters<ColumnId>)
}

type TableFilterArgs<
  D extends object,
  ColumnId extends string,
  Context extends object | undefined = undefined,
> = {
  stateModel: DataTableStateModel<D>
  columns: Array<Column<D, ColumnId, Context>>
}

export const reatomDataTableFilters = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>({
  initialFilters = {},
  columns,
  stateModel,
}: TableFilterOptions<ColumnId> & TableFilterArgs<D, ColumnId, Context>) => {
  const filtersAtom = atom<TableFilter.TableFilters<ColumnId>>(
    {},
    'filtersAtom',
  ).pipe(
    withInit((ctx) => {
      if (typeof initialFilters === 'function') {
        return initialFilters(ctx)
      }
      return initialFilters
    }),
  )

  const applyClientSideFiltersAction = action(async (ctx) => {
    const filters = ctx.get(filtersAtom)

    const items = (ctx.get(stateModel).value?.data ?? []) as Array<
      D & {
        [key in ColumnId]: unknown
      }
    >
    const composedFilter = TableFilter.composeFilters(filters)
    if (!composedFilter) {
      return
    }

    const filteredItems = items.filter((item) =>
      TableFilter.test(composedFilter, columns)(item),
    )

    stateModel.setState(ctx, AsyncStateN.success({ data: filteredItems }))
  }, 'applyClientSideFiltersAction')

  return filtersAtom.pipe(
    withAssign(() => ({
      applyClientSideFilters: applyClientSideFiltersAction,
    })),
  )
}
